/**
 * Dashboard API integration (CMS-SGD-* SG dashboard, operational dashboard when available).
 */
import {
  getMockStatusChart,
  getMockSummary,
  getOperationalMockCases,
  MOCK_AUDIT_LOGS,
  MOCK_CATEGORY_CHART,
  MOCK_DASHBOARD_CASES,
  MOCK_URGENCY_CHART,
} from '../data/dashboardMockData';
import type {
  DashboardAuditLogEntry,
  DashboardCaseSearchRequest,
  DashboardCaseSearchResponse,
  DashboardChartResponse,
  DashboardDateRange,
  DashboardSummaryResponse,
  DashboardVariant,
} from '../types/dashboard';
import { dashboardApiRequest } from './apiClient';
import {
  adaptDashboardCaseSearchPage,
  adaptDashboardSummary,
  adaptFileCategoryChart,
  adaptStatusChart,
  adaptTimelineResponse,
  adaptUrgencyChart,
  buildDashboardSearchBody,
  type ApiDashboardCaseSearchPage,
  type ApiDashboardSummaryResponse,
  type ApiFileCategoryChartResponse,
  type ApiStatusChartResponse,
  type ApiTimelineResponse,
  type ApiUrgencyChartResponse,
} from './dashboardAdapter';

/** Use live APIs by default; only use mock if explicitly enabled via environment variables. */
export function useDashboardMock(variant: DashboardVariant): boolean {
  if ((import.meta.env.VITE_USE_MOCK_API ?? '').trim() === 'true') return true;
  if (variant === 'operational') {
    return (import.meta.env.VITE_USE_OPERATIONAL_DASHBOARD_MOCK ?? 'false').trim() === 'true';
  }
  return false;
}

function dashboardBase(variant: DashboardVariant): string {
  return variant === 'sg' ? '/v1/dashboard/sg' : '/v1/dashboard/operational';
}

function withDateRangeQuery(path: string, range: DashboardDateRange): string {
  const qs = new URLSearchParams({ from: range.from, to: range.to });
  return `${path}?${qs.toString()}`;
}

function filterMockCases(
  rows: typeof MOCK_DASHBOARD_CASES,
  req: DashboardCaseSearchRequest,
): DashboardCaseSearchResponse {
  let filtered = rows.filter((row) => {
    const submitted = new Date(row.submittedAt);
    const from = new Date(req.from);
    const to = new Date(req.to);
    to.setHours(23, 59, 59, 999);
    return submitted >= from && submitted <= to;
  });

  if (req.columnFilters) {
    for (const [key, raw] of Object.entries(req.columnFilters)) {
      const q = raw.trim().toLowerCase();
      if (!q) continue;
      filtered = filtered.filter((row) => {
        const val = String((row as Record<string, unknown>)[key] ?? '').toLowerCase();
        return val.includes(q);
      });
    }
  }

  if (req.sortBy) {
    const dir = req.sortDirection === 'DESC' ? -1 : 1;
    filtered = [...filtered].sort((a, b) => {
      const av = String((a as Record<string, unknown>)[req.sortBy!] ?? '');
      const bv = String((b as Record<string, unknown>)[req.sortBy!] ?? '');
      return av.localeCompare(bv) * dir;
    });
  }

  const totalElements = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / req.size));
  const page = Math.min(req.page, totalPages);
  const start = (page - 1) * req.size;
  const content = filtered.slice(start, start + req.size);

  return {
    content,
    page,
    size: req.size,
    totalElements,
    totalPages,
  };
}

export async function fetchDashboardSummary(
  variant: DashboardVariant,
  range: DashboardDateRange,
): Promise<DashboardSummaryResponse> {
  if (useDashboardMock(variant)) {
    void range;
    return getMockSummary(variant);
  }
  const res = await dashboardApiRequest<ApiDashboardSummaryResponse>(
    withDateRangeQuery(`${dashboardBase(variant)}/summary`, range),
    { method: 'GET' },
  );
  if (!res.ok) throw new Error(res.message);
  return adaptDashboardSummary(res.data, variant);
}

export async function fetchDashboardUrgencyChart(
  variant: DashboardVariant,
  range: DashboardDateRange,
): Promise<DashboardChartResponse> {
  if (useDashboardMock(variant)) {
    void variant;
    void range;
    return MOCK_URGENCY_CHART;
  }
  const res = await dashboardApiRequest<ApiUrgencyChartResponse>(
    withDateRangeQuery(`${dashboardBase(variant)}/charts/urgency`, range),
    { method: 'GET' },
  );
  if (!res.ok) throw new Error(res.message);
  return adaptUrgencyChart(res.data);
}

export async function fetchDashboardStatusChart(
  variant: DashboardVariant,
  range: DashboardDateRange,
): Promise<DashboardChartResponse> {
  if (useDashboardMock(variant)) {
    void range;
    return getMockStatusChart(variant);
  }
  const res = await dashboardApiRequest<ApiStatusChartResponse>(
    withDateRangeQuery(`${dashboardBase(variant)}/charts/status`, range),
    { method: 'GET' },
  );
  if (!res.ok) throw new Error(res.message);
  return adaptStatusChart(res.data, variant);
}

export async function fetchDashboardCategoryChart(
  variant: DashboardVariant,
  range: DashboardDateRange,
): Promise<DashboardChartResponse> {
  if (useDashboardMock(variant)) {
    void variant;
    void range;
    return MOCK_CATEGORY_CHART;
  }
  const res = await dashboardApiRequest<ApiFileCategoryChartResponse>(
    withDateRangeQuery(`${dashboardBase(variant)}/charts/file-category`, range),
    { method: 'GET' },
  );
  if (!res.ok) throw new Error(res.message);
  return adaptFileCategoryChart(res.data);
}

export async function searchDashboardCases(
  variant: DashboardVariant,
  req: DashboardCaseSearchRequest,
): Promise<DashboardCaseSearchResponse> {
  if (useDashboardMock(variant)) {
    const rows = variant === 'sg' ? MOCK_DASHBOARD_CASES : getOperationalMockCases();
    return filterMockCases(rows, req);
  }

  const apiPage = Math.max(0, req.page - 1);
  const qs = new URLSearchParams({
    page: String(apiPage),
    size: String(req.size),
  });
  const res = await dashboardApiRequest<ApiDashboardCaseSearchPage>(
    `${dashboardBase(variant)}/cases/search?${qs.toString()}`,
    {
      method: 'POST',
      body: buildDashboardSearchBody(req),
    },
  );
  if (!res.ok) throw new Error(res.message);
  return adaptDashboardCaseSearchPage(res.data);
}

export async function fetchCaseAuditLogs(
  variant: DashboardVariant,
  caseRequestId: string,
): Promise<DashboardAuditLogEntry[]> {
  if (useDashboardMock(variant)) {
    void caseRequestId;
    const byRef = MOCK_DASHBOARD_CASES.find((c) => c.caseRequestId === caseRequestId);
    if (byRef && MOCK_AUDIT_LOGS[byRef.fileRef]) {
      return MOCK_AUDIT_LOGS[byRef.fileRef];
    }
    return [
      {
        date: '—',
        time: '—',
        name: 'System',
        role: 'Automated Event',
        isSystem: true,
        action: 'No audit log entries',
        badgeVariant: 'blue',
      },
    ];
  }

  const res = await dashboardApiRequest<ApiTimelineResponse>(
    `${dashboardBase(variant)}/cases/${encodeURIComponent(caseRequestId)}/timeline`,
    { method: 'GET' },
  );
  if (!res.ok) throw new Error(res.message);
  const entries = adaptTimelineResponse(res.data);
  return entries.length
    ? entries
    : [
        {
          date: '—',
          time: '—',
          name: 'System',
          role: 'Automated Event',
          isSystem: true,
          action: 'No audit log entries',
          badgeVariant: 'blue',
        },
      ];
}
